import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  diagnosisCategoriesService,
  CreateDiagnosisCategoryInput,
  UpdateDiagnosisCategoryInput,
} from "../../services/admin/diagnosiscategories.service";

export function useDiagnosisCategoriesAdmin() {
  return useQuery({
    queryKey: ["admin", "diagnosis-categories"],
    queryFn: diagnosisCategoriesService.getAll,
  });
}

export function useDiagnosisCategory(id?: string) {
  return useQuery({
    queryKey: ["admin", "diagnosis-categories", id],
    queryFn: () => diagnosisCategoriesService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateDiagnosisCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateDiagnosisCategoryInput) =>
      diagnosisCategoriesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "diagnosis-categories"],
      });
      queryClient.invalidateQueries({ queryKey: ["diagnosis-categories"] });
      notifications.show({
        title: "Success",
        message: "Diagnosis Category created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create diagnosis category",
        color: "red",
      });
    },
  });
}

export function useUpdateDiagnosisCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateDiagnosisCategoryInput) =>
      diagnosisCategoriesService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "diagnosis-categories"],
      });
      queryClient.invalidateQueries({ queryKey: ["diagnosis-categories"] });
      notifications.show({
        title: "Success",
        message: "Diagnosis Category updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update diagnosis category",
        color: "red",
      });
    },
  });
}

export function useDeleteDiagnosisCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => diagnosisCategoriesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "diagnosis-categories"],
      });
      queryClient.invalidateQueries({ queryKey: ["diagnosis-categories"] });
      notifications.show({
        title: "Success",
        message: "Diagnosis Category deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete diagnosis category",
        color: "red",
      });
    },
  });
}

export function useToggleDiagnosisCategoryActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      diagnosisCategoriesService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "diagnosis-categories"],
      });
      queryClient.invalidateQueries({
        queryKey: ["diagnosis-categories"],
      });
      queryClient.invalidateQueries({ queryKey: ["diagnosis"] });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}
